import InterVariableWoff2 from '@fontsource-variable/inter/files/inter-latin-wght-normal.woff2';
import RobotoRegularWoff2 from '@fontsource/roboto/files/roboto-latin-400-normal.woff2';
import RobotoMediumWoff2 from '@fontsource/roboto/files/roboto-latin-500-normal.woff2';
import SFProRoundedMediumWoff from 'themes/fonts/SFProRounded/SFProRoundedMedium.woff';
import SFProRoundedMediumWoff2 from 'themes/fonts/SFProRounded/SFProRoundedMedium.woff2';
import { TPartnerFonts } from 'partners/types';

/**
 * InstaView fonts — roles confirmed by the product team:
 *
 *   --font-title    Inter           titles
 *   --font-body     Roboto          all other text
 *   --font-caption  SF Pro Rounded  warning / info / confirmation messages
 *                                   ("this field is required", "I agree to
 *                                   the terms and policy")
 *
 * Self-hosted via @fontsource rather than the Google Fonts CDN: the app runs
 * in a webview that may be offline or behind a strict CSP, and a third-party
 * font request would add a round trip to first paint on the low-end Android
 * devices we care about.
 *
 * Inter is the variable build — one 100-900 file covers Regular (label3),
 * SemiBold (title2/3, CTAs) and Bold (title1), smaller than three static cuts.
 *
 * ⚠ See ./typography.ts: which *variants* carry the caption role does not yet
 * match the product description — the messages product named use `body2`
 * today, while label1/label2 are used for headers.
 */

/** Fallbacks appended to every stack, in platform order. */
const SYSTEM_FALLBACK = [
  '-apple-system', // macOS/iOS
  'BlinkMacSystemFont', // older Chrome/Safari
  '"Segoe UI"', // Windows
  'Roboto', // Android
  '"Helvetica Neue"', // Mac
  'Arial',
  'sans-serif'
];

/** Joins a stack, dropping any fallback the face already leads with. */
const stack = (...preferred: string[]): string =>
  [...preferred, ...SYSTEM_FALLBACK.filter((f) => !preferred.includes(f))].join(
    ', '
  );

const FONT_TITLE = stack('"Inter Variable"', 'Inter');
const FONT_BODY = stack('Roboto');
const FONT_CAPTION = stack('"SF Pro Rounded"');

const fonts: TPartnerFonts = {
  stacks: {
    // MUI derives h1-h6, button, caption, subtitle and overline from `base`.
    // Those are "other text" in the product's split, so they follow the body
    // face; titles get Inter explicitly through the title1-3 / CTA variants.
    base: FONT_BODY,
    baseline: FONT_BODY,
    title: FONT_TITLE,
    body: FONT_BODY,
    caption: FONT_CAPTION
  },
  faces: [
    {
      // Variable: one file serves 400 (label3), 600 (title2/3, CTAs) and
      // 700 (title1) with real outlines rather than synthesised ones.
      family: 'Inter Variable',
      weight: '100 900',
      woff2: InterVariableWoff2
    },
    { family: 'Roboto', weight: 400, woff2: RobotoRegularWoff2 },
    { family: 'Roboto', weight: 500, woff2: RobotoMediumWoff2 },
    {
      family: 'SF Pro Rounded',
      weight: 500,
      woff2: SFProRoundedMediumWoff2,
      woff: SFProRoundedMediumWoff
    }
  ]
};

export default fonts;
